/* jk26-intro — Gorilla cinematic: first-entry video → snowball impact → white-out → page reveal
 * --------------------------------------------------------------------------
 * Isolated component. Owns only #jk26-intro and the html classes
 * `jk26-intro-lock` (scroll lock) / `jk26-revealed` (hero entrance trigger).
 * Nothing here touches the theme header, cart, analytics or other pages.
 *
 * States:  idle → loading → playing → impact → whiteout → revealing → completed
 *          any state ──skip──▶ revealing          any failure ──fallback──▶ revealing
 *
 * Triggers for "impact" (whichever comes first):
 *   1. video frame time ≥ cfg.impactTime           (measured marker, default 3.20 s)
 *   2. sampled frame is white (mean luma ≥ cfg.lumaThreshold) when autoDetect is on
 *   3. `ended` event                               (clip finished without either)
 * Safety nets: loadTimeout (no playable media), stall watchdog, maxTotal.
 * Reduced motion, Save-Data, missing media or a blocked autoplay never trap
 * the visitor: the page is revealed at once. No "seen" flag is stored: every
 * new navigation or reload plays the intro again, exactly as briefed.
 * -------------------------------------------------------------------------- */
(function () {
  "use strict";
  const root = document.getElementById("jk26-intro");
  if (!root) return;
  if (window.JK26 && window.JK26.intro && window.JK26.intro.state !== "idle") return;   // already running (asset included twice)

  const cfg = Object.assign({
    enabled: true, impactTime: 3.2, whiteHold: 0.32, revealDuration: 1.15, loadTimeout: 3500, maxTotal: 9000,
    autoDetect: true, lumaThreshold: 0.86, sources: {}, debug: false,
  }, (window.JK26_CONFIG && window.JK26_CONFIG.intro) || {}, { debug: !!(window.JK26_CONFIG && window.JK26_CONFIG.debug) });

  const html = document.documentElement;
  const video = root.querySelector(".jk26-intro__video");
  const skipBtn = root.querySelector(".jk26-intro__skip");
  const status = root.querySelector(".jk26-intro__status");
  const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const saveData = !!(navigator.connection && navigator.connection.saveData);

  const api = { state: "idle", log: [], timings: { init: performance.now() }, impactSource: null, reason: null };
  window.JK26 = window.JK26 || {};
  window.JK26.intro = api;

  const t0 = performance.now();
  let timers = [];
  let detached = false;
  let rvfcHandle = 0;
  let rafHandle = 0;
  let sampler = null;
  let lastProgressAt = 0;

  function log(msg, extra) {
    const entry = { t: Math.round(performance.now() - t0), state: api.state, msg, ...(extra || {}) };
    api.log.push(entry);
    if (cfg.debug) console.debug("[jk26-intro]", entry.t + "ms", msg, extra || "");
  }
  function setState(s) {
    api.state = s;
    root.dataset.state = s;
    api.timings[s] = performance.now() - t0;
    emit("jk26:intro-state", { state: s });
  }
  function emit(name, detail) { document.dispatchEvent(new CustomEvent(name, { detail: Object.assign({ api }, detail || {}) })); }
  function later(fn, ms) { const id = setTimeout(fn, ms); timers.push(id); return id; }
  function clearTimers() { timers.forEach(clearTimeout); timers = []; }

  function lock() { html.classList.add("jk26-intro-lock"); }
  function unlock() { html.classList.remove("jk26-intro-lock"); }

  /* ---- choose the smallest adequate rendition --------------------------- */
  function pickSource() {
    const s = cfg.sources || {};
    const small = Math.min(window.innerWidth, window.innerHeight) * (window.devicePixelRatio || 1) <= 820;
    const canWebm = !!(video.canPlayType && video.canPlayType('video/webm; codecs="vp9"'));
    const canMp4 = !!(video.canPlayType && video.canPlayType('video/mp4; codecs="avc1.640028"'));
    /* H.264 MP4 first wherever it plays: every phone decodes it in hardware, and iPhones (which may also say yes to
       WebM) start it reliably, also in Low Power Mode. WebM only where MP4 cannot play (e.g. a test browser). */
    const order = [];
    if (canMp4) order.push(small ? ["mp4720", "mp41080"] : ["mp41080", "mp4720"]);
    if (canWebm) order.push(small ? ["webm720", "webm1080"] : ["webm1080", "webm720"]);
    if (!canMp4 && !canWebm) order.push(small ? ["mp4720", "mp41080"] : ["mp41080", "mp4720"]);
    for (const keys of order) for (const k of keys) if (s[k]) return { src: s[k], type: k.startsWith("webm") ? "video/webm" : "video/mp4", key: k };
    return null;
  }

  /* ---- frame watching ---------------------------------------------------- */
  function startFrameWatch() {
    if (cfg.autoDetect) {
      try {
        const c = document.createElement("canvas"); c.width = 24; c.height = 24;
        const ctx = c.getContext("2d", { willReadFrequently: true });
        sampler = { c, ctx, every: 2, n: 0 };
      } catch { sampler = null; }
    }
    const onFrame = (now, meta) => {
      if (detached || api.state !== "playing") return;
      const t = meta && typeof meta.mediaTime === "number" ? meta.mediaTime : video.currentTime;
      lastProgressAt = performance.now();
      if (t >= cfg.impactTime) return impact("timestamp", t);
      if (sampler && (++sampler.n % sampler.every === 0) && t > cfg.impactTime * 0.6) {
        try {
          sampler.ctx.drawImage(video, 0, 0, 24, 24);
          const d = sampler.ctx.getImageData(0, 0, 24, 24).data;
          let sum = 0, min = 255;
          for (let i = 0; i < d.length; i += 4) { const l = (d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000; sum += l; if (l < min) min = l; }
          const mean = sum / (d.length / 4) / 255;
          if (mean >= cfg.lumaThreshold && min / 255 >= cfg.lumaThreshold - 0.3) return impact("luma", t, { mean: +mean.toFixed(3) });
        } catch { sampler = null; /* tainted canvas etc. → timestamp path only */ }
      }
      schedule();
    };
    const schedule = () => {
      if ("requestVideoFrameCallback" in video) rvfcHandle = video.requestVideoFrameCallback(onFrame);
      else rafHandle = requestAnimationFrame(() => onFrame(performance.now(), null));
    };
    schedule();
  }
  function stopFrameWatch() {
    try { if (rvfcHandle && video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(rvfcHandle); } catch { /* ignore */ }
    if (rafHandle) cancelAnimationFrame(rafHandle);
    rvfcHandle = 0; rafHandle = 0;
  }

  /* ---- seamless start -------------------------------------------------------
     A still of the clip's first frame (.jk26-intro__still) covers the video until the video shows a frame from its
     very beginning while playing; then the video takes over («is-live»): the same picture, nothing jumps.
     Two ways to know a frame is up, whichever answers first:
       · requestVideoFrameCallback: the frame actually presented (and its media time);
       · timeupdate: the clock has moved (for browsers without frame callbacks, or where they never fire; the
         WebKit build used in tests has the method but never calls it).
     A clip that starts late (an iPhone showed its first frame at 0.43 s in tests: the gorilla "jumped") is rewound
     to 0 under the still, once, and takes over when the frame at 0 is ready again (the next presented frame, or
     "seeked" where frames are not reported). Frames shown while paused (a preloaded first frame) do not count. */
  let firstRun = 0;
  function watchFirstFrame() {
    const run = ++firstRun;
    let live = false, seeking = false, rewound = false, framesReported = false;
    const goLive = (why, t) => {
      if (live || run !== firstRun) return;
      live = true;
      root.classList.add("is-live");
      api.timings.live = performance.now() - t0;
      log("live", { why, mediaTime: +(t || 0).toFixed(3) });
    };
    const done = () => live || run !== firstRun || api.state === "revealing" || api.state === "completed";
    const onFrame = (t, via) => {                             // → true when finished
      if (done()) return true;
      if (seeking || video.paused) return false;
      if (t <= 0.12 || rewound) { goLive(rewound ? "rewound" : "first-frame", t); return true; }
      rewound = seeking = true;
      log("rewind", { mediaTime: +t.toFixed(3), via });
      video.addEventListener("seeked", () => {
        seeking = false;
        if (!framesReported) goLive("seeked", video.currentTime);   // no frame callbacks: the frame at 0 is ready now
      }, { once: true });
      try { video.currentTime = 0; } catch { seeking = false; }
      return false;
    };
    if ("requestVideoFrameCallback" in video) {
      const cb = (now, meta) => {
        framesReported = true;
        if (!onFrame(meta && typeof meta.mediaTime === "number" ? meta.mediaTime : video.currentTime, "frame")) video.requestVideoFrameCallback(cb);
      };
      video.requestVideoFrameCallback(cb);
    }
    const tu = () => {
      if (done() || framesReported) { video.removeEventListener("timeupdate", tu); return; }
      if (video.currentTime > 0 && onFrame(video.currentTime, "clock")) video.removeEventListener("timeupdate", tu);
    };
    video.addEventListener("timeupdate", tu);
    /* never a frozen still: if nothing was reported 1.5 s after playback began, the video shows anyway */
    video.addEventListener("playing", () => setTimeout(() => { if (api.state === "playing") goLive("timeout", video.currentTime); }, 1500), { once: true });
  }

  /* ---- transitions -------------------------------------------------------- */
  function impact(source, t, extra) {
    if (api.state !== "playing") return;
    stopFrameWatch();
    api.impactSource = source;
    api.timings.impactMediaTime = t;
    setState("impact");
    log("impact", Object.assign({ source, mediaTime: +(t || 0).toFixed(3) }, extra || {}));
    root.classList.add("is-impact");                       // flash layer → opacity 1 (CSS, 220 ms)
    emit("jk26:intro-impact", { source, mediaTime: t });
    later(() => { setState("whiteout"); later(() => reveal("impact"), Math.max(0, cfg.whiteHold * 1000)); }, 220);
  }

  function reveal(reason, durationMs) {
    if (api.state === "revealing" || api.state === "completed") return;
    clearTimers();
    stopFrameWatch();
    api.reason = api.reason || reason;
    setState("revealing");
    log("reveal", { reason });
    root.classList.add("is-impact", "is-revealing");
    try { video.pause(); } catch { /* ignore */ }
    unlock();
    html.classList.add("jk26-revealed");                   // hero entrance starts under the receding white
    emit("jk26:intro-reveal", { reason });

    const dur = Math.max(250, durationMs || cfg.revealDuration * 1000);
    const start = performance.now();
    const ease = (x) => 1 - Math.pow(1 - x, 3.2);           // heavy ease-out: fast open, soft landing
    const step = () => {
      const p = Math.min(1, (performance.now() - start) / dur);
      root.style.setProperty("--jk26-r", (ease(p) * 160).toFixed(2));
      if (p < 1) requestAnimationFrame(step); else complete();
    };
    requestAnimationFrame(step);
  }

  function complete() {
    if (api.state === "completed") return;
    setState("completed");
    log("completed", { total: Math.round(performance.now() - t0) });
    try { video.removeAttribute("src"); video.load(); } catch { /* ignore */ }
    root.setAttribute("hidden", "");
    root.setAttribute("inert", "");
    root.style.display = "none";
    unlock();
    html.classList.add("jk26-revealed", "jk26-intro-done");
    const main = document.querySelector("main, [data-jk26-main]");
    const focusWasInOverlay = document.activeElement === root || root.contains(document.activeElement) || document.activeElement === document.body;
    if (main && focusWasInOverlay) {            /* hand keyboard focus to the page without scrolling it */
      try { main.setAttribute("tabindex", "-1"); main.focus({ preventScroll: true }); } catch { /* ignore */ }
    }
    emit("jk26:intro-complete", { reason: api.reason, timings: api.timings });
  }

  function fallback(reason, detail) {
    if (api.state === "revealing" || api.state === "completed") return;
    log("fallback", Object.assign({ reason }, detail || {}));
    api.reason = reason;
    reveal(reason, 600);
  }

  function skip() {
    if (api.state === "revealing" || api.state === "completed") return;
    log("skip");
    api.reason = "skip";
    reveal("skip", 650);
  }

  /* ---- boot ---------------------------------------------------------------- */
  function start() {
    const pick = pickSource();
    if (!cfg.enabled) return finishInstantly("disabled");
    if (reduceMotion) return finishInstantly("reduced-motion");
    if (saveData) return finishInstantly("save-data");
    if (!pick) return finishInstantly("no-source");

    lock();
    /* refined loading-screen framing: container size is configurable per device (Lab → Gorilla-intro) */
    root.style.setProperty("--jk26-introbox-d", ((cfg.size && cfg.size.d) || 520) + "px");
    root.style.setProperty("--jk26-introbox-m", ((cfg.size && cfg.size.m) || 300) + "px");
    setState("loading");
    log("loading", pick);
    root.removeAttribute("hidden");
    try { root.focus({ preventScroll: true }); } catch { /* ignore */ }
    setTimeout(() => { if (skipBtn && api.state !== "completed") skipBtn.classList.add("is-visible"); }, 600); // plain timer: must survive clearTimers()

    skipBtn && skipBtn.addEventListener("click", skip);
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") skip(); }, { once: false });

    /* absolute safety nets */
    later(() => fallback("load-timeout", { readyState: video.readyState }), cfg.loadTimeout);
    later(() => { if (api.state !== "completed") fallback("max-total"); }, cfg.maxTotal);

    video.muted = true; video.defaultMuted = true; video.playsInline = true; video.setAttribute("playsinline", "");
    video.addEventListener("error", () => fallback("media-error", { code: video.error && video.error.code }));
    video.addEventListener("stalled", () => later(() => { if (api.state === "playing" && performance.now() - lastProgressAt > 1500) fallback("stalled"); }, 1600));
    video.addEventListener("waiting", () => later(() => { if (api.state === "playing" && performance.now() - lastProgressAt > 1500) fallback("waiting"); }, 1600));
    video.addEventListener("ended", () => { if (api.state === "playing") impact("ended", video.currentTime); });
    /* the clock also counts, for browsers whose frame callbacks never fire: a moving clock is progress (else the stall
       watchdog above would end the intro after any "waiting", e.g. the one a rewind causes), and the impact marker is
       taken from it too (else the white-out would wait for the end of the clip), at most one timeupdate (≈ 0.25 s)
       later than a frame callback would have it */
    let lastClock = -1;
    video.addEventListener("timeupdate", () => {
      if (api.state !== "playing") return;
      if (video.currentTime !== lastClock) { lastClock = video.currentTime; lastProgressAt = performance.now(); }
      if (video.currentTime >= cfg.impactTime) impact("clock", video.currentTime);
    });

    /* Playback is requested AT ONCE, not after "canplay": an iPhone loads no video data before play() is called,
       so waiting for canplay ran into the load timeout and the intro never appeared on iOS. Muted + playsinline
       autoplay is allowed on iOS Safari and Android Chrome; when a browser still refuses (Low Power Mode, a
       setting) the page is revealed at once. canplay/loadeddata ask again for browsers that want data first. */
    const toPlaying = () => {
      if (api.state !== "loading") return;
      clearTimers();                                      // load timeout no longer applies
      later(() => { if (api.state !== "completed") fallback("max-total"); }, cfg.maxTotal);
      setState("playing");
      lastProgressAt = performance.now();
      log("playing", { duration: +(video.duration || 0).toFixed(3), w: video.videoWidth, h: video.videoHeight });
      if (status) status.textContent = "";
      startFrameWatch();
    };
    const tryPlay = (why) => {
      if (api.state !== "loading") return;
      if (why !== "start") api.timings[why] = performance.now() - t0;
      let p;
      try { p = video.play(); } catch (err) { log("play threw", { why, err: String(err && err.name) }); return; }
      if (p && typeof p.then === "function") {
        p.then(toPlaying).catch((err) => {
          /* NotAllowedError = autoplay refused → the page at once; AbortError = a load interrupted this try,
             the next canplay/loadeddata asks again */
          if (err && err.name === "NotAllowedError") fallback("autoplay-blocked", { err: err.name, why });
          else log("play retry later", { why, err: String(err && err.name) });
        });
      }
    };
    video.addEventListener("playing", toPlaying);
    video.addEventListener("canplay", () => tryPlay("canplay"), { once: true });
    video.addEventListener("loadeddata", () => tryPlay("loadeddata"), { once: true });

    const src = document.createElement("source");
    src.src = pick.src; src.type = pick.type;
    /* with <source> children the failure event fires on the LAST source, not on <video> */
    src.addEventListener("error", () => fallback("media-error", { source: pick.key, networkState: video.networkState }));
    video.appendChild(src);
    watchFirstFrame();
    video.load();
    tryPlay("start");
  }

  function finishInstantly(reason) {
    api.reason = reason;
    log("bypass", { reason });
    setState("revealing");
    unlock();
    root.style.setProperty("--jk26-r", "160");
    complete();
  }

  /* dev / tests: replay the whole sequence on demand (not exposed in the UI) */
  api.replay = function replay() {
    detached = false; clearTimers(); stopFrameWatch();
    api.state = "idle"; api.log = []; api.reason = null; api.impactSource = null; api.timings = { init: performance.now() };
    root.classList.remove("is-impact", "is-revealing", "is-live");
    root.removeAttribute("hidden"); root.removeAttribute("inert"); root.style.display = ""; root.style.removeProperty("--jk26-r");
    html.classList.remove("jk26-revealed", "jk26-intro-done");
    skipBtn && skipBtn.classList.remove("is-visible");
    video.querySelectorAll("source").forEach((s) => s.remove());
    cfg.enabled = true;
    window.scrollTo({ top: 0, behavior: "instant" });
    start();
  };
  api.skip = skip;

  start();
})();
